import { Button } from '@alison/ui';
import type { SvgIconComponent } from '@mui/icons-material';
import AddOutlined from '@mui/icons-material/AddOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { addFilterButtonSx, FILTER_MENU_SLOT_PROPS, filterMenuItemSx } from './menuStyles';

export interface FilterMenuFamily {
  id: string;
  label: string;
  tone: string;
  badge?: string;
}

export interface FilterMenuEntry {
  key: string;
  label: string;
  icon: SvgIconComponent;
  family: string;
}

interface FilterMenuProps {
  families: FilterMenuFamily[];
  entries: FilterMenuEntry[];
  activeKeys: Set<string>;
  disabledReason: (key: string) => string | null;
  onAdd: (key: string) => void;
  leadingItem?: (close: () => void) => ReactNode;
}

export function FilterMenu({
  families,
  entries,
  activeKeys,
  disabledReason,
  onAdd,
  leadingItem,
}: FilterMenuProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const close = (): void => setAnchor(null);

  const toggleFamily = (familyId: string): void => {
    setExpandedFamilies((previous) => {
      const next = new Set(previous);
      if (next.has(familyId)) next.delete(familyId);
      else next.add(familyId);
      return next;
    });
  };

  const groups = families
    .map((family) => ({
      family,
      definitions: entries
        .filter((entry) => entry.family === family.id)
        .sort((first, second) => first.label.localeCompare(second.label)),
    }))
    .filter((group) => group.definitions.length > 0);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AddOutlined sx={{ fontSize: 14 }} />}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          setAnchor(event.currentTarget);
          setExpandedFamilies(
            new Set(
              groups
                .filter((group) =>
                  group.definitions.some((definition) => activeKeys.has(definition.key)),
                )
                .map((group) => group.family.id),
            ),
          );
        }}
        sx={addFilterButtonSx}
      >
        Add Filter
      </Button>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={close}
        slotProps={FILTER_MENU_SLOT_PROPS}
      >
        {leadingItem?.(close)}
        {groups.flatMap(({ family, definitions }, index) => {
          const expanded = expandedFamilies.has(family.id);
          return [
            <ListSubheader
              key={`${family.id}-header`}
              disableSticky
              disableGutters
              sx={(theme) => ({
                mt: 0.5,
                pt: 0.5,
                ...(index > 0
                  ? { borderTop: '1px solid', borderColor: theme.palette.divider }
                  : {}),
                bgcolor: 'transparent',
              })}
            >
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => toggleFamily(family.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleFamily(family.id);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  px: 2,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'background.platform' },
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: family.tone }} />
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    fontSize: 9,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                  }}
                >
                  {family.label}
                </Box>
                {family.badge === undefined ? null : (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 16,
                      fontSize: 8,
                      fontWeight: 800,
                      color: 'text.secondary',
                      bgcolor: 'background.platform',
                      borderRadius: 0.5,
                      px: 1,
                    }}
                  >
                    {family.badge}
                  </Box>
                )}
                <ExpandMoreOutlined
                  sx={{
                    fontSize: 14,
                    color: 'text.disabled',
                    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </Box>
            </ListSubheader>,
            ...(expanded
              ? definitions.map((definition) => {
                  const Icon = definition.icon;
                  const used = activeKeys.has(definition.key);
                  const reason = used ? null : disabledReason(definition.key);
                  const item = (
                    <MenuItem
                      key={definition.key}
                      disabled={used || reason !== null}
                      onClick={() => {
                        onAdd(definition.key);
                        close();
                      }}
                      sx={filterMenuItemSx}
                    >
                      <ListItemIcon>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={definition.label} />
                      {used ? (
                        <Typography variant="caption" color="text.disabled">
                          Added
                        </Typography>
                      ) : null}
                    </MenuItem>
                  );
                  if (reason === null) return item;
                  return (
                    <Tooltip
                      key={definition.key}
                      title={reason}
                      placement="top"
                      disableInteractive
                      slotProps={{
                        popper: {
                          modifiers: [{ name: 'offset', options: { offset: [0, -10] } }],
                        },
                      }}
                    >
                      <Box component="span" sx={{ display: 'block' }}>
                        {item}
                      </Box>
                    </Tooltip>
                  );
                })
              : []),
          ];
        })}
      </Menu>
    </>
  );
}
